import { Injectable, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ChatMessage } from '../models/chat';
import { SignalrService } from '../../../core/services/signalr.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../shared/services/notification.service';
import { AuthService } from '../../../core/services/auth.service';
import { UserRole } from '../enums/userRole.enum';

@Injectable({
  providedIn: 'root'
})
export class ChatHubService extends SignalrService {

  private chatClosed = new BehaviorSubject<boolean>(false);
  private chatAssigned = new BehaviorSubject<boolean>(false);
  private groupId = new BehaviorSubject<string>('');
  private agentAvailable = new BehaviorSubject<boolean>(false);
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  private rejoinedChat = new BehaviorSubject<boolean>(false);
  private chatRequested = new BehaviorSubject<boolean>(false);
  private welcomeMessageSent = new BehaviorSubject<boolean>(false);
  private chatProjectId = new BehaviorSubject<number>(0);
  private projectName = new BehaviorSubject<string>('');

  public chatClosed$ = this.chatClosed.asObservable();
  public chatAssigned$ = this.chatAssigned.asObservable();
  public groupId$ = this.groupId.asObservable();
  public agentAvailable$ = this.agentAvailable.asObservable();
  public messages$ = this.messagesSubject.asObservable();
  public rejoinedChat$ = this.rejoinedChat.asObservable();
  public chatRequested$ = this.chatRequested.asObservable();
  public welcomeMessageSent$ = this.welcomeMessageSent.asObservable();
  public chatProjectId$ = this.chatProjectId.asObservable();
  public projectName$ = this.projectName.asObservable();

  constructor(
    private translate: TranslateService,
    private notificationService: NotificationService,
    private authService: AuthService,
  ) {
    super('chathub');
  }

  protected override listenForEvents(): void {
    this.hubConnection.on('ChatClosed', () => {
      this.chatClosed.next(true);
      this.resetChatState();
    });

    this.hubConnection.on('AgentAssignedToChat', (groupId: string) => {
      this.handleNewAssignment(groupId);
    });

    this.hubConnection.on('ChatAssigned', (groupId: string, userId: number, projectId: number) => {
      this.chatProjectId.next(projectId);
      this.handleNewAssignment(groupId);
    });

    this.hubConnection.on('RejoinedChat', (groupId: string, chat: ChatMessage[], projectId: number) => {
      this.chatProjectId.next(projectId);
      this.handleRejoinedChat(groupId, chat);
    });

    this.hubConnection.on('NoAgentAvailable', () => {
      this.agentAvailable.next(false);
    });

    this.hubConnection.off('ReceiveMessage');
    this.hubConnection.on('ReceiveMessage', async (chat: ChatMessage) => {
      if (!chat) return;
      this.messagesSubject.next([...this.messagesSubject.getValue(), chat]);
      if (chat.senderId !== this.authService.currentUser.id) {
        const isClient = await this.authService.hasPermission(UserRole[UserRole.Client]);
        this.notificationService.incrementUnreadMessageCount(chat.message, this.projectName.getValue(), isClient);
        setTimeout(() => this.notificationService.hideMessageNotification(), 3000);
      }
    });
  }

  resetChatState(): void {
    this.rejoinedChat.next(false);
    this.groupId.next('');
    this.chatClosed.next(false);
    this.chatRequested.next(false);
    this.chatAssigned.next(false);
    this.agentAvailable.next(false);
    this.messagesSubject.next([]);
    this.welcomeMessageSent.next(false);
  }

  private handleNewAssignment(groupId: string): void {
    this.agentAvailable.next(true);
    this.groupId.next(groupId);
    this.chatAssigned.next(true);
    this.welcomeMessageSent.next(false);
  }

  private handleRejoinedChat(groupId: string, chat: ChatMessage[]): void {
    if (chat.length !== 0) this.messagesSubject.next(chat);
    this.groupId.next(groupId);
    this.rejoinedChat.next(true);
    this.agentAvailable.next(true);
    this.chatAssigned.next(true);
  }

  public async closeLiveChat(chatId: string, userId: number, projectId: number): Promise<void> {
    if (!chatId) this.chatRequested.next(false);
    await this.ensureConnection();
    if (this.isConnected()) {
      await this.hubConnection.invoke('CloseLiveChat', chatId, userId, projectId);
      if (!this.chatAssigned.getValue())
        this.resetChatState();
    }
  }

  public async requestAgent(projectId: number, clientId: number): Promise<void> {
    await this.ensureConnection();
    if (this.isConnected()) {
      await this.hubConnection.invoke('AssignChatToAgent', projectId, clientId, null);
      this.chatRequested.next(true);
    }
  }

  public async sendMessage(chat: ChatMessage): Promise<void> {
    if (!chat?.groupId || !chat?.senderId) return;
    await this.ensureConnection();
    if (this.isConnected()) {
      await this.hubConnection.invoke('SendMessage', chat);
    }
  }

  public async sendWelcomeMessage(groupId: string, senderId: number): Promise<void> {
    if (this.welcomeMessageSent.getValue() ||
      !this.chatAssigned.getValue() ||
      !groupId ||
      this.chatClosed.getValue()) {
      return;
    }

    await this.ensureConnection();
    if (this.isConnected()) {
      const welcomeMessage: ChatMessage = {
        groupId: groupId,
        message: this.translate.instant('chat.firstMessage'),
        senderId: senderId,
        createdDate: new Date(),
        createdBy: senderId,
        lastModifiedBy: senderId,
      };

      await this.hubConnection.invoke('SendMessage', welcomeMessage);
      this.welcomeMessageSent.next(true);
    }
  }

  setProjectName(name: string) {
    this.projectName.next(name);
  }

}
